/**
 * Video of a Knowledge Area document (video ingest plan, WEB-1).
 *
 * Plays by byte range through a short-lived stream URL (TQS-4
 * `POST /documents/{id}/stream-token`, BFF-1 public stream route), so a long
 * video starts at once and a jump to 42:10 loads only that part. A backend
 * without the route answers 404/422 and the renderer falls back to what the
 * app did before: the Bearer download of the whole file as an object URL —
 * only up to `MAX_BLOB_BYTES` (100 MiB); a larger video is never pulled into
 * memory. "Herunterladen" streams to disk where the browser allows it.
 *
 * Below the player: what the Brain knows about the video — duration,
 * speakers, chapters and the AI-written summary, each marked as machine made.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ArrowDownToLine, Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MediaPlayer } from '@/components/applications/multimedia/MediaPlayer'
import { cdbTqsApiService } from '@/services/cdb-tqs-api-service'
import {
  FileTooLargeForBrowserError,
  MAX_BLOB_BYTES,
  createVideoStreamToken,
  downloadDocumentFile,
  parseDocumentVideoInfo,
  type DocumentVideoInfo,
} from '@/services/cdb-tqs/video'
import { useDocumentThumbnail } from '@/hooks/useDocumentThumbnail'
import { useRevokeObjectUrl } from '@/hooks/useRevokeObjectUrl'
import { formatTimecode } from '@/lib/utils/timecode'
import { logError } from '@/lib/logger'

export interface SeekRequest {
  t: number
  nonce: number
}

interface VideoRendererProps {
  /** KA document id: enables streaming, poster and the video facts. */
  documentId?: string
  fileName: string
  /** Already resolved URL (Drive blob, older callers); used without a stream token. */
  fileUrl?: string
  seekRequest?: SeekRequest
  /** Current position, e.g. to persist `t` in the window's deep link. */
  onTimeChange?: (seconds: number) => void
  /** Hide the facts section (small embeds). */
  showFacts?: boolean
}

type Mode = 'stream' | 'blob' | 'direct'

interface Source {
  src: string
  mode: Mode
  captions?: { src: string; srclang: string; label: string }[]
}

type Phase = 'loading' | 'ready' | 'unplayable' | 'failed' | 'tooLarge'

/** MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED — the codec or container. */
const SRC_NOT_SUPPORTED = 4
/** Playback counts as resumed once it ran this far past the re-mint point. */
const RESUMED_AFTER_S = 0.5

export function VideoRenderer({
  documentId,
  fileName,
  fileUrl,
  seekRequest,
  onTimeChange,
  showFacts = true,
}: VideoRendererProps) {
  const { t, i18n } = useTranslation()
  const [source, setSource] = useState<Source | null>(null)
  const [phase, setPhase] = useState<Phase>('loading')
  const [attempt, setAttempt] = useState(0)
  const [ownedBlobUrl, setOwnedBlobUrl] = useState<string | null>(null)
  const [activeSeek, setActiveSeek] = useState<SeekRequest | undefined>(seekRequest)
  const [info, setInfo] = useState<DocumentVideoInfo | null>(null)
  const [autoplay, setAutoplay] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const lastTimeRef = useRef(0)
  const isPlayingRef = useRef(false)
  // Set while a re-minted stream has not played yet: a second error then is
  // real. Cleared once playback moved on, so the next idle expiry recovers too.
  const streamRetriedRef = useRef(false)
  const remintAtRef = useRef(0)
  useRevokeObjectUrl(ownedBlobUrl)

  useEffect(() => {
    if (seekRequest) setActiveSeek(seekRequest)
  }, [seekRequest?.t, seekRequest?.nonce]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    streamRetriedRef.current = false
  }, [documentId, fileUrl])

  // Resolve a playable URL: stream token first, the old download as fallback.
  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false
    setPhase('loading')

    const resolve = async (): Promise<Source | null> => {
      if (documentId) {
        const token = await createVideoStreamToken(documentId, controller.signal)
        if (token) {
          return {
            src: token.url,
            mode: 'stream',
            captions: token.captionsUrl
              ? [{ src: token.captionsUrl, srclang: i18n.language?.slice(0, 2) || 'de', label: t('video.player.captions') }]
              : undefined,
          }
        }
        if (fileUrl) return { src: fileUrl, mode: 'direct' }
        const blobUrl = await cdbTqsApiService.getDocumentFile(documentId, fileName, controller.signal, {
          maxBytes: MAX_BLOB_BYTES,
        })
        if (cancelled) {
          URL.revokeObjectURL(blobUrl)
          return null
        }
        setOwnedBlobUrl(blobUrl)
        return { src: blobUrl, mode: 'blob' }
      }
      return fileUrl ? { src: fileUrl, mode: 'direct' } : null
    }

    resolve()
      .then((next) => {
        if (cancelled) return
        setSource(next)
        setPhase(next ? 'ready' : 'failed')
      })
      .catch((err: unknown) => {
        if (cancelled || controller.signal.aborted) return
        if (err instanceof FileTooLargeForBrowserError) {
          setPhase('tooLarge')
          return
        }
        logError(err as Error, { context: 'VideoRenderer', action: 'resolveSource', documentId })
        setPhase('failed')
      })
    return () => {
      cancelled = true
      controller.abort()
    }
    // t/i18n only label the caption track; a language switch needs no refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, fileName, fileUrl, attempt])

  // What the Brain knows about the video. Missing on older backends → nothing shown.
  useEffect(() => {
    if (!documentId || !showFacts) {
      setInfo(null)
      return
    }
    let cancelled = false
    cdbTqsApiService
      .getDocument(documentId)
      .then((doc: unknown) => {
        if (cancelled) return
        const raw = doc && typeof doc === 'object' ? (doc as Record<string, unknown>).video : null
        setInfo(parseDocumentVideoInfo(raw))
      })
      .catch(() => {
        if (!cancelled) setInfo(null)
      })
    return () => {
      cancelled = true
    }
  }, [documentId, showFacts])

  const poster = useDocumentThumbnail(documentId, 640, !!documentId && source?.mode === 'stream')

  const handleTime = useCallback(
    (seconds: number) => {
      lastTimeRef.current = seconds
      if (streamRetriedRef.current && seconds > remintAtRef.current + RESUMED_AFTER_S) {
        streamRetriedRef.current = false
      }
      onTimeChange?.(seconds)
    },
    [onTimeChange],
  )

  const handlePlay = useCallback(() => {
    isPlayingRef.current = true
  }, [])
  const handlePause = useCallback(() => {
    isPlayingRef.current = false
  }, [])

  const handleError = useCallback(
    (error: unknown) => {
      const code = (error as MediaError | undefined)?.code
      // An idle stream token expires (sliding TTL, 15 min). Mint a new one and
      // continue where playback stopped — playing again if it was playing. One
      // try per expiry: the guard resets once playback has moved on.
      if (source?.mode === 'stream' && !streamRetriedRef.current) {
        streamRetriedRef.current = true
        remintAtRef.current = lastTimeRef.current
        setAutoplay(isPlayingRef.current)
        setActiveSeek((prev) => ({ t: lastTimeRef.current, nonce: (prev?.nonce ?? 0) + 1 }))
        setAttempt((a) => a + 1)
        return
      }
      setPhase(code === SRC_NOT_SUPPORTED ? 'unplayable' : 'failed')
    },
    [source?.mode],
  )

  const handleDownload = useCallback(async () => {
    if (!documentId && !fileUrl) return
    setDownloadError(null)
    // A given URL is already in this tab (Drive blob): hand it over as is.
    if (!documentId) {
      const link = document.createElement('a')
      link.href = fileUrl!
      link.download = fileName
      link.click()
      return
    }
    try {
      // Streams to disk where the browser can; never a 2 GiB Blob.
      await downloadDocumentFile(documentId, fileName)
    } catch (err) {
      if (err instanceof FileTooLargeForBrowserError) {
        setDownloadError(t('video.player.downloadTooLarge', { maxMb: Math.round(err.maxBytes / (1024 * 1024)) }))
        return
      }
      logError(err as Error, { context: 'VideoRenderer', action: 'download', documentId })
      setDownloadError(t('video.player.downloadFailed'))
    }
  }, [documentId, fileName, fileUrl, t])

  const retry = useCallback(() => {
    streamRetriedRef.current = false
    setAutoplay(false)
    setActiveSeek((prev) => ({ t: lastTimeRef.current, nonce: (prev?.nonce ?? 0) + 1 }))
    setAttempt((a) => a + 1)
  }, [])

  const jumpTo = useCallback((seconds: number) => {
    setActiveSeek((prev) => ({ t: seconds, nonce: (prev?.nonce ?? 0) + 1 }))
  }, [])

  let stage: ReactNode
  if (phase === 'loading') {
    stage = (
      <div className="flex flex-1 items-center justify-center" role="status">
        <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none text-[var(--text-secondary)]" aria-hidden="true" />
        <span className="sr-only">{t('video.player.loading')}</span>
      </div>
    )
  } else if (phase === 'unplayable' || phase === 'failed' || phase === 'tooLarge' || !source) {
    stage = (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center" role="alert">
        <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
        <p className="max-w-sm text-sm text-[var(--text-color)]">
          {phase === 'unplayable'
            ? t('video.player.unplayable')
            : phase === 'tooLarge'
              ? t('video.player.tooLargeToPlay', { maxMb: Math.round(MAX_BLOB_BYTES / (1024 * 1024)) })
              : t('video.player.loadFailed')}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {phase !== 'unplayable' && phase !== 'tooLarge' && (
            <Button variant="outline" size="sm" className="gap-2" onClick={retry}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {t('video.player.retry')}
            </Button>
          )}
          {(documentId || fileUrl) && (
            <Button variant="ghost" size="sm" className="gap-2" onClick={() => void handleDownload()}>
              <ArrowDownToLine className="h-4 w-4" aria-hidden="true" />
              {t('video.player.download')}
            </Button>
          )}
        </div>
        {downloadError && (
          <p className="max-w-sm text-xs text-destructive" aria-live="polite">
            {downloadError}
          </p>
        )}
      </div>
    )
  } else {
    stage = (
      <MediaPlayer
        key={source.src}
        src={source.src}
        type="video"
        poster={poster ?? undefined}
        captions={source.captions}
        seekRequest={activeSeek}
        autoplay={autoplay}
        fallbackDuration={info?.duration_s}
        ariaLabel={t('video.player.regionNamed', { name: fileName })}
        className="h-full w-full"
        onTimeUpdate={handleTime}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={handleError}
      />
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-3" data-testid="video-renderer">
      {/* 16:9 stage: sized by the width, so it also works in block-level
          containers such as the chat's split preview; capped for tall windows. */}
      <div className="flex aspect-video max-h-[75vh] min-h-[180px] w-full shrink-0 items-stretch">{stage}</div>
      {showFacts && info && <VideoFacts info={info} onJump={jumpTo} />}
    </div>
  )
}

function VideoFacts({ info, onJump }: { info: DocumentVideoInfo; onJump: (seconds: number) => void }) {
  const { t } = useTranslation()
  const facts: string[] = []
  if (info.duration_s !== undefined) facts.push(t('video.facts.duration', { time: formatTimecode(info.duration_s) }))
  if (info.speakers !== undefined && info.speakers > 0) facts.push(t('video.facts.speakers', { count: info.speakers }))
  if (info.has_audio === false) facts.push(t('video.facts.noAudio'))

  return (
    <section
      aria-label={t('video.facts.heading')}
      className="max-h-[40%] shrink-0 space-y-2 overflow-auto rounded-lg border border-[var(--window-border-color)] bg-[var(--window-background)] p-3 text-sm"
    >
      {facts.length > 0 && <p className="text-xs text-[var(--text-secondary)]">{facts.join(' · ')}</p>}
      <p className="text-xs text-[var(--text-secondary)]">{t('video.facts.provenance')}</p>
      {info.summary && (
        <p className="leading-relaxed text-[var(--text-color)]">
          <span className="mr-1 inline-flex items-center gap-1 font-medium" title={t('video.facts.summaryHint')}>
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            {t('video.facts.summaryLabel')}:
          </span>
          {info.summary}
        </p>
      )}
      {info.chapters && (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-[var(--text-secondary)]">{t('video.facts.chapters')}</h4>
          <ul className="flex flex-wrap gap-1.5">
            {info.chapters.map((chapter) => (
              <li key={`${chapter.start_s}-${chapter.title}`}>
                <button
                  type="button"
                  onClick={() => onJump(chapter.start_s)}
                  className="rounded border border-[var(--window-border-color)] px-2 py-0.5 text-xs text-[var(--text-color)] hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]"
                  aria-label={t('video.player.jumpTo', { time: formatTimecode(chapter.start_s) }) + ` – ${chapter.title}`}
                >
                  <span className="font-mono">{formatTimecode(chapter.start_s)}</span> {chapter.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

export default VideoRenderer
